import { ApiError, toApiError } from './errors';

/**
 * Địa chỉ gốc của API.
 *
 * Đọc từ biến môi trường lúc dựng để bản dựng thật trỏ đúng máy chủ. Giá trị mặc định là
 * cổng của ứng dụng chủ khi chạy trên máy phát triển.
 */
const baseUrl: string = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5080';

/**
 * Phiếu truy cập của phiên hiện tại.
 *
 * Giữ trong bộ nhớ, KHÔNG lưu vào localStorage hay sessionStorage. Lý do: mọi thứ lưu ở đó đều
 * đọc được bằng JavaScript, nên một lỗ hổng chèn mã ở bất kỳ đâu trên trang là đủ để lấy phiếu.
 *
 * Cái giá phải trả: tải lại trang là mất phiếu. Không thành vấn đề vì refresh token nằm trong
 * cookie mà JavaScript không đọc được, nên giao diện lấy lại phiếu mới bằng một lời gọi im lặng.
 */
let accessToken: string | null = null;

/** Hàm được gọi khi phiên hết hiệu lực, để giao diện đưa người dùng về trang đăng nhập. */
let onSessionExpired: (() => void) | null = null;

/** Đặt phiếu truy cập cho các lời gọi sau. */
export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/** Khai hàm xử lý khi phiên hết hiệu lực. */
export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

/** Tùy chọn của một lời gọi API. */
interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  readonly body?: unknown;

  /**
   * Bỏ qua bước tự làm mới phiên khi nhận mã 401.
   *
   * Bắt buộc đặt cho chính lời gọi làm mới phiên, nếu không sẽ thành vòng lặp vô hạn:
   * làm mới thất bại thì lại gọi làm mới.
   */
  readonly skipRefresh?: boolean;
}

/** Kết quả của một lần thử làm mới phiên đang chạy. */
/**
 * Phản hồi của một lần làm mới phiên thành công.
 *
 * Khai ở đây chứ không ở tầng nghiệp vụ xác thực, vì cơ chế chống gọi trùng nằm ở tệp này và nó
 * phải trả về ĐÚNG một kết quả dùng chung cho mọi nơi gọi — kể cả nơi cần thông tin người dùng chứ
 * không chỉ cần phiếu truy cập.
 */
export interface RefreshedSession {
  readonly accessToken: string;
  readonly accessTokenExpiresAt: string;
  readonly userId: string;
  readonly userName: string;
  readonly fullName: string;
  readonly companyId: string;
  readonly mustChangePassword: boolean;
}

let inFlightRefresh: Promise<RefreshedSession | null> | null = null;

/**
 * Gọi một điểm cuối của API.
 *
 * Khi nhận mã 401, hàm này tự thử làm mới phiên đúng một lần rồi gọi lại. Nhờ vậy người dùng
 * không bị đẩy ra trang đăng nhập mỗi mười lăm phút, và mã gọi API ở các trang không phải
 * tự xử lý việc đó.
 */
export async function request<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const response = await send(path, options);

  if (response.status !== 401 || options.skipRefresh === true) {
    return await readResponse<TResponse>(response);
  }

  const refreshed = await refreshOnce();

  if (!refreshed) {
    onSessionExpired?.();

    throw await toApiError(response);
  }

  return await readResponse<TResponse>(await send(path, options));
}

/** Gửi một yêu cầu HTTP tới API. */
async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken !== null) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return await fetch(`${baseUrl}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? null : JSON.stringify(options.body),

    // Bắt buộc để trình duyệt gửi kèm cookie chứa refresh token. Không có tùy chọn này thì
    // việc làm mới phiên luôn thất bại, và người dùng bị đăng xuất sau mười lăm phút.
    credentials: 'include',
  });
}

/**
 * Gọi một điểm cuối không trả về nội dung.
 *
 * Tách thành hàm riêng thay vì gọi hàm chung với kiểu rỗng: kiểu rỗng dùng làm tham số kiểu là
 * cách viết mà bộ kiểm tra mã nguồn từ chối, và nó từ chối có lý — kiểu rỗng chỉ có nghĩa ở vị trí
 * kiểu trả về.
 */
export async function requestNoContent(
  path: string,
  options: RequestOptions = {},
): Promise<void> {
  await request<unknown>(path, options);
}

/**
 * Gửi một biểu mẫu nhiều phần — dùng cho việc tải tệp lên.
 *
 * <b>Không tự đặt tiêu đề kiểu nội dung.</b> Trình duyệt phải tự đặt nó, vì nó cần kèm theo chuỗi
 * ranh giới ngăn cách các phần — chuỗi mà chỉ trình duyệt biết. Đặt tay tiêu đề ấy là cách chắc
 * chắn làm máy chủ không đọc được biểu mẫu.
 */
export async function requestForm<TResponse>(
  path: string,
  form: FormData,
): Promise<TResponse> {
  const response = await sendForm(path, form);

  if (response.status !== 401) {
    return await readResponse<TResponse>(response);
  }

  const refreshed = await refreshOnce();

  if (!refreshed) {
    onSessionExpired?.();

    throw await toApiError(response);
  }

  return await readResponse<TResponse>(await sendForm(path, form));
}

/** Gửi một biểu mẫu nhiều phần kèm phiếu xác thực. */
async function sendForm(path: string, form: FormData): Promise<Response> {
  const headers: Record<string, string> = {};

  if (accessToken !== null) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return await fetch(`${baseUrl}${path}`, {
    method: 'POST',
    headers,
    body: form,
    credentials: 'include',
  });
}

/**
 * Tải một tệp về dưới dạng khối nhị phân.
 *
 * <b>Phải đi qua đây chứ không đặt thẳng đường dẫn vào thẻ liên kết.</b> Điểm cuối tải tệp đòi
 * phiếu xác thực trong tiêu đề, mà một thẻ liên kết thì không gửi tiêu đề nào — nó sẽ nhận về lỗi
 * từ chối và người dùng thấy một trang lỗi thay vì tệp của mình.
 */
export async function requestBlob(path: string): Promise<Blob> {
  const response = await sendBlob(path);

  if (response.status !== 401) {
    if (!response.ok) {
      throw await toApiError(response);
    }

    return await response.blob();
  }

  const refreshed = await refreshOnce();

  if (!refreshed) {
    onSessionExpired?.();

    throw await toApiError(response);
  }

  const retried = await sendBlob(path);

  if (!retried.ok) {
    throw await toApiError(retried);
  }

  return await retried.blob();
}

/** Gửi một yêu cầu tải tệp kèm phiếu xác thực. */
async function sendBlob(path: string): Promise<Response> {
  const headers: Record<string, string> = {};

  if (accessToken !== null) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return await fetch(`${baseUrl}${path}`, { headers, credentials: 'include' });
}

/** Đọc nội dung phản hồi, hoặc ném lỗi nếu phản hồi không thành công. */
async function readResponse<TResponse>(response: Response): Promise<TResponse> {
  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  return (await response.json()) as TResponse;
}

/**
 * Làm mới phiên, bảo đảm chỉ có <b>một</b> lời gọi làm mới chạy tại một thời điểm.
 *
 * Cần thiết vì một trang thường gọi nhiều điểm cuối cùng lúc. Nếu tất cả cùng nhận 401 và cùng gọi
 * làm mới, thì lời gọi thứ hai sẽ gửi lên một refresh token vừa bị lời gọi thứ nhất dùng mất — và
 * máy chủ hiểu đó là dấu hiệu token bị đánh cắp, rồi thu hồi cả họ token.
 *
 * Nói cách khác: thiếu phần này thì cơ chế phát hiện đánh cắp của máy chủ sẽ liên tục đăng xuất
 * người dùng thật.
 *
 * <b>MỌI nơi cần làm mới phiên phải đi qua đây</b>, kể cả bước phục hồi phiên lúc khởi động ứng
 * dụng. Bản đầu để bước phục hồi gọi thẳng vào điểm cuối, và hậu quả lộ ra ngay ở chế độ phát
 * triển: React gắn hiệu ứng hai lần nên bước phục hồi chạy hai lần, lần thứ hai gửi lên token vừa
 * bị dùng, và người dùng bị đăng xuất ở mỗi lần tải trang.
 */
export async function refreshSessionOnce(): Promise<RefreshedSession | null> {
  inFlightRefresh ??= (async () => {
    try {
      const session = await request<RefreshedSession>('/api/v1/auth/refresh', {
        method: 'POST',
        skipRefresh: true,
      });

      setAccessToken(session.accessToken);

      return session;
    } catch (error) {
      if (error instanceof ApiError) {
        return null;
      }

      throw error;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return await inFlightRefresh;
}

/** Làm mới phiên và chỉ cho biết thành công hay không. Dùng ở đường thử lại sau khi nhận 401. */
async function refreshOnce(): Promise<boolean> {
  return (await refreshSessionOnce()) !== null;
}
